import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_MAX_ITERATIONS,
  DEFAULT_NUM_COMPONENTS,
  DEFAULT_SCALE,
  DEFAULT_TOLERANCE,
  INPUT_NAMES,
} from '@/tools/partialLeastSquaresV2/partialLeastSquares.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { MappedInputFeatureV1 } from '@/tools/models/templateInstance.utilities';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
} from '@/tools/tool.types';

export interface PartialLeastSquaresV2StoreState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  outputsCollapsed: boolean;
  modelPropertiesExpanded: boolean;
  numComponents: number;
  scale: boolean;
  maxIterations: number;
  tolerance: number;
  inputSignals: ToolItem[];
  targetSignal?: ToolItem | null;
  condition?: ToolItem | null;
  restrictToCondition?: ToolItem | null;
  tableApplicationId?: string;
  instanceId?: string;
}

export interface PartialLeastSquaresV2StoreActions {
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setOutputsCollapsed: (collapsed: boolean) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setModelPropertiesExpanded: (expanded: boolean) => void;
  setNumComponents: (numComponents: number) => void;
  setScale: (scale: boolean) => void;
  setMaxIterations: (maxIterations: number) => void;
  setTolerance: (tolerance: number) => void;
  addInputSignal: (signal: ToolItem) => void;
  removeInputSignal: (signal: ToolItem) => void;
  setTargetSignal: (signal: ToolItem | null | undefined) => void;
  setCondition: (condition: ToolItem | null | undefined) => void;
  setRestrictToCondition: (condition: ToolItem | null | undefined) => void;
  rehydrateForEdit: (payload: ModelRehydratePayload) => void;
  reset: ResettableStoreState<PartialLeastSquaresV2StoreState>;
}

export type PartialLeastSquaresDehydratedState = PartialLeastSquaresV2StoreState;

const ITEM_PARAMETER_KEYS = ['inputSignals', 'targetSignal', 'condition', 'restrictToCondition'];

const initialState: PartialLeastSquaresV2StoreState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  windowStart: 0,
  windowEnd: 0,
  outputsCollapsed: true,
  modelPropertiesExpanded: false,
  numComponents: DEFAULT_NUM_COMPONENTS,
  scale: DEFAULT_SCALE,
  maxIterations: DEFAULT_MAX_ITERATIONS,
  tolerance: DEFAULT_TOLERANCE,
  inputSignals: [] as ToolItem[],
  targetSignal: undefined as ToolItem | null | undefined,
  condition: undefined as ToolItem | null | undefined,
  restrictToCondition: undefined as ToolItem | null | undefined,
  tableApplicationId: undefined as string | undefined,
  instanceId: undefined as string | undefined,
});

const partialLeastSquaresV2ConfigParamsSelector = createSelector(
  [(state: PartialLeastSquaresV2StoreState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.PARTIAL_LEAST_SQUARES_V_2,
      parameterDefinitionKeys: ITEM_PARAMETER_KEYS,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });
    return _.omit(config, ['modelPropertiesExpanded']) as ToolConfigParams;
  },
);

const partialLeastSquaresV2WindowSelector = createSelector(
  [
    (state: PartialLeastSquaresV2StoreState) => state.windowStart,
    (state: PartialLeastSquaresV2StoreState) => state.windowEnd,
  ],
  (windowStart, windowEnd) => ({ startTime: windowStart, endTime: windowEnd }),
);

export const dehydratePartialLeastSquaresV2StoreState = (
  state: PartialLeastSquaresV2StoreState,
): PartialLeastSquaresDehydratedState => state;

const partialLeastSquaresV2ActionsFactory: ActionsFactory<
  PartialLeastSquaresV2StoreState,
  PartialLeastSquaresV2StoreActions
> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setOutputsCollapsed: (collapsed) =>
    set((draft) => {
      draft.outputsCollapsed = collapsed;
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setModelPropertiesExpanded: (expanded) =>
    set((draft) => {
      draft.modelPropertiesExpanded = expanded;
    }),

  setNumComponents: (numComponents) =>
    set((draft) => {
      draft.numComponents = numComponents;
    }),

  setScale: (scale) =>
    set((draft) => {
      draft.scale = scale;
    }),

  setMaxIterations: (maxIterations) =>
    set((draft) => {
      draft.maxIterations = maxIterations;
    }),

  setTolerance: (tolerance) =>
    set((draft) => {
      draft.tolerance = tolerance;
    }),

  addInputSignal: (signal) =>
    set((draft) => {
      const sanitized = sanitizeToolItem(signal);
      if (sanitized && !draft.inputSignals.some((s) => s.id === sanitized.id)) {
        draft.inputSignals = draft.inputSignals.concat(sanitized);
      }
    }),

  removeInputSignal: (signal) =>
    set((draft) => {
      draft.inputSignals = draft.inputSignals.filter((s) => s.id !== signal?.id);
    }),

  setTargetSignal: (item) =>
    set((draft) => {
      draft.targetSignal = sanitizeToolItem(item) as PartialLeastSquaresV2StoreState['targetSignal'];
    }),

  setCondition: (item) =>
    set((draft) => {
      draft.condition = sanitizeToolItem(item) as PartialLeastSquaresV2StoreState['condition'];
    }),

  setRestrictToCondition: (item) =>
    set((draft) => {
      draft.restrictToCondition = sanitizeToolItem(item) as PartialLeastSquaresV2StoreState['restrictToCondition'];
    }),

  rehydrateForEdit: (payload: ModelRehydratePayload) => {
    resetState();
    const inputSignals: ToolItem[] = [];
    set((draft) => {
      draft.id = payload.id;
      draft.instanceId = payload.instanceId;
      draft.tableApplicationId = payload.instanceData?.tableApplicationId;
      (payload.modelData?.inputFeatures ?? []).forEach((feature: MappedInputFeatureV1): void => {
        const inputName: string = feature.name!;
        switch (inputName) {
          case SeeqNames.Models.InstanceNameInputFeatureName:
            // TODO: CRAB-53864: Handle duplicate names more gracefully
            draft.name = payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`;
            break;
          case INPUT_NAMES.nComponentsInput:
            draft.numComponents = feature.inputNumeric ?? DEFAULT_NUM_COMPONENTS;
            break;
          case INPUT_NAMES.scaleInput:
            draft.scale = feature.inputBoolean ?? DEFAULT_SCALE;
            break;
          case INPUT_NAMES.maxIterationsInput:
            draft.maxIterations = feature.inputNumeric ?? DEFAULT_MAX_ITERATIONS;
            break;
          case INPUT_NAMES.toleranceInput:
            draft.tolerance = feature.inputNumeric ?? DEFAULT_TOLERANCE;
            break;
          case INPUT_NAMES.startTimeInput: {
            const startTimeInput = JSON.parse(feature.inputText!);
            draft.windowStart = moment(startTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.endTimeInput: {
            const endTimeInput = JSON.parse(feature.inputText!);
            draft.windowEnd = moment(endTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.trainWithinConditionInput:
            if (feature.inputItem) {
              draft.condition = { id: feature.inputItem };
            }
            break;
          case INPUT_NAMES.restrictOutputToConditionInput: {
            if (feature.inputItem) {
              draft.restrictToCondition = { id: feature.inputItem };
            }
            break;
          }
          case INPUT_NAMES.targetSignalInput: {
            if (feature.inputItem) {
              draft.targetSignal = { id: feature.inputItem };
            }
            break;
          }
          default:
            if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
              inputSignals.push({ id: feature.inputItem });
            }
            break;
        }
      });
      draft.inputSignals = inputSignals;
    });
  },

  reset: (overrides) => resetState(overrides),
});

const [
  PartialLeastSquaresV2StoreProvider,
  usePartialLeastSquaresV2StoreInstance,
  usePartialLeastSquaresV2StoreSelector,
] = createStore('PartialLeastSquaresV2', initialState, partialLeastSquaresV2ActionsFactory);

export { PartialLeastSquaresV2StoreProvider };

export const usePartialLeastSquaresV2ToolId = () => usePartialLeastSquaresV2StoreSelector((state) => state.id);
export const usePartialLeastSquaresV2ToolName = () => usePartialLeastSquaresV2StoreSelector((state) => state.name);
export const usePartialLeastSquaresV2ToolOriginalParameters = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.originalParameters);
export const usePartialLeastSquaresV2ToolAdvancedParametersCollapsed = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.advancedParametersCollapsed);
export const usePartialLeastSquaresV2ToolOutputsCollapsed = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.outputsCollapsed);
export const usePartialLeastSquaresV2ToolWindow = () =>
  usePartialLeastSquaresV2StoreSelector(partialLeastSquaresV2WindowSelector);
export const usePartialLeastSquaresV2ToolModelPropertiesExpanded = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.modelPropertiesExpanded);
export const usePartialLeastSquaresV2ToolNumComponents = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.numComponents);
export const usePartialLeastSquaresV2ToolScale = () => usePartialLeastSquaresV2StoreSelector((state) => state.scale);
export const usePartialLeastSquaresV2ToolMaxIterations = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.maxIterations);
export const usePartialLeastSquaresV2ToolTolerance = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.tolerance);
export const usePartialLeastSquaresV2ToolInputSignals = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.inputSignals);
export const usePartialLeastSquaresV2ToolTargetSignal = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.targetSignal);
export const usePartialLeastSquaresV2ToolCondition = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.condition);
export const usePartialLeastSquaresV2ToolRestrictToCondition = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.restrictToCondition);
export const usePartialLeastSquaresV2ToolInstanceId = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.instanceId);
export const usePartialLeastSquaresV2ToolTableApplicationId = () =>
  usePartialLeastSquaresV2StoreSelector((state) => state.tableApplicationId);
export const usePartialLeastSquaresV2ToolConfigParams = () =>
  usePartialLeastSquaresV2StoreSelector(partialLeastSquaresV2ConfigParamsSelector);

export const usePartialLeastSquaresV2ToolActions = () => usePartialLeastSquaresV2StoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestPartialLeastSquaresV2Store(overrides?: Partial<PartialLeastSquaresV2StoreState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestPartialLeastSquaresV2Store() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, partialLeastSquaresV2ActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (utility files)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreatePartialLeastSquaresV2Store() {
  return createStoreInstance<PartialLeastSquaresV2StoreState, PartialLeastSquaresV2StoreActions>(
    initialState,
    partialLeastSquaresV2ActionsFactory,
  );
}

export const usePersistPartialLeastSquaresV2Store = () => {
  const register = useRegisterPersistedStore();
  const store = usePartialLeastSquaresV2StoreInstance();

  useEffect(() => {
    return register({
      storeName: 'partialLeastSquaresV2Store',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydratePartialLeastSquaresV2StoreState,
    });
  }, [register, store]);
};
